import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { AdminTab } from '@/components/admin/AdminSidebar';
import AdminOverview from '@/components/admin/AdminOverview';
import AdminUsers from '@/components/admin/AdminUsers';
import AdminUsage from '@/components/admin/AdminUsage';
import AdminPayments from '@/components/admin/AdminPayments';
import AdminHealth from '@/components/admin/AdminHealth';
import BroadcastEmails from '@/components/admin/BroadcastEmails';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [userInsights, setUserInsights] = useState<any[]>([]);
  const [businessStats, setBusinessStats] = useState<any>(null);
  const [systemErrors, setSystemErrors] = useState<any[]>([]);
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const { data: users } = await supabase
        .from('view_ai_cost_per_user')
        .select('*')
        .order('total_cost_usd', { ascending: false });
        
      const { data: bStats } = await supabase
        .from('view_platform_business_stats')
        .select('*')
        .single();
      
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: payErrors } = await supabase
        .from('dead_letter_transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (users) setUserInsights(users);
      if (bStats) setBusinessStats(bStats);
      if (profiles) setAllProfiles(profiles);
      if (payErrors) setSystemErrors(payErrors);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {isLoading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
              Aggregating Telemetry...
            </p>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <AdminOverview 
              businessStats={businessStats} 
              allProfiles={allProfiles} 
              systemErrors={systemErrors}
              setActiveTab={setActiveTab}
            />
          )}
          {activeTab === 'users' && <AdminUsers allProfiles={allProfiles} />}
          {activeTab === 'usage' && <AdminUsage userInsights={userInsights} />}
          {activeTab === 'payments' && <AdminPayments setActiveTab={setActiveTab} />}
          {activeTab === 'health' && <AdminHealth systemErrors={systemErrors} />}
          {activeTab === 'broadcast' && <BroadcastEmails />}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
